// Admin MFA gate — shown ABOVE the admin dashboard. Two states:
//  1. Setup: admin hasn't enabled MFA yet → show QR code + verification input.
//  2. Challenge: admin enabled MFA but current JWT doesn't carry mfa_passed
//     (i.e. fresh login) → ask for the 6-digit code.
// On success, calls onPass() so the parent (AdminPage) renders normal content.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMfaGate({ onPass }) {
  const [phase, setPhase] = useState("checking"); // "checking" | "setup" | "challenge" | "passed"
  const [otpauthUri, setOtpauthUri] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/mfa/status`);
        if (res.data?.enabled) {
          // MFA is configured for this account → always challenge. Forces
          // re-verification on every page load, which is the desired admin UX
          // (no "remember this device" by design).
          setPhase("challenge");
        } else {
          setPhase("setup");
        }
      } catch (err) {
        // /admin/mfa/status only 200s for admins; for non-admins it 403s.
        // Either way we don't want to nuke their session — just hide the gate
        // and let AdminPage's own admin-only guard render its 403 message.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("MFA status check failed", err?.response?.status, err?.response?.data);
        }
        setPhase("setup");
      }
    })();
  }, [onPass]);

  const startSetup = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/mfa/setup`);
      setSecret(res.data?.secret);
      setOtpauthUri(res.data?.otpauth_uri);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start MFA setup");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (path) => {
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}${path}`, { code });
      if (res.data?.token) {
        // Promote the JWT — same cookie storage path used by the OTP flow.
        localStorage.setItem("token", res.data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
      }
      toast.success(path.includes("/enable") ? "MFA enabled. Future logins will require your code." : "MFA verified");
      setPhase("passed");
      onPass();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "checking" || phase === "passed") return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6" data-testid="admin-mfa-gate">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg p-8">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
          <ShieldCheck className="w-7 h-7 text-[#D4AF37]" />
        </div>

        {phase === "setup" && !otpauthUri && (
          <>
            <h2 className="text-white font-serif text-2xl text-center mb-2">Set up Admin MFA</h2>
            <p className="text-white/60 text-sm text-center mb-6 leading-relaxed">
              Two-factor authentication is required for the admin panel.
              You'll need an authenticator app (Google Authenticator, Authy, or 1Password).
            </p>
            <Button
              data-testid="mfa-setup-start"
              onClick={startSetup}
              disabled={submitting}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-60"
            >
              {submitting ? "Generating…" : "Generate QR code"}
            </Button>
          </>
        )}

        {phase === "setup" && otpauthUri && (
          <>
            <h2 className="text-white font-serif text-2xl text-center mb-2">Scan with your authenticator</h2>
            <p className="text-white/60 text-xs text-center mb-4">
              Then enter the 6-digit code below to confirm setup.
            </p>
            <div className="bg-white p-3 rounded mb-4 flex items-center justify-center">
              <img
                alt="MFA QR"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(otpauthUri)}`}
                className="w-60 h-60"
              />
            </div>
            <p className="text-white/40 text-[10px] text-center mb-4 font-mono break-all">
              Or enter secret manually: <span className="text-[#D4AF37]/80">{secret}</span>
            </p>
            <Input
              data-testid="mfa-setup-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              inputMode="numeric"
              autoFocus
              className="bg-white/5 border-white/10 text-white text-center text-xl tracking-[0.5em] mb-3"
            />
            <Button
              data-testid="mfa-setup-confirm"
              onClick={() => submitCode("/admin/mfa/enable")}
              disabled={submitting || code.length !== 6}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-60"
            >
              {submitting ? "Verifying…" : "Enable MFA"}
            </Button>
          </>
        )}

        {phase === "challenge" && (
          <>
            <h2 className="text-white font-serif text-2xl text-center mb-2">Enter your MFA code</h2>
            <p className="text-white/60 text-sm text-center mb-6">
              Open your authenticator app and enter the current 6-digit code for <span className="text-[#D4AF37]">Travelsate Admin</span>.
            </p>
            <Input
              data-testid="mfa-challenge-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) submitCode("/admin/mfa/challenge"); }}
              placeholder="6-digit code"
              inputMode="numeric"
              autoFocus
              className="bg-white/5 border-white/10 text-white text-center text-xl tracking-[0.5em] mb-3"
            />
            <Button
              data-testid="mfa-challenge-submit"
              onClick={() => submitCode("/admin/mfa/challenge")}
              disabled={submitting || code.length !== 6}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-60"
            >
              {submitting ? "Verifying…" : "Verify"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
